import { Link } from "@tanstack/react-router";
import { ArrowUpRight, MessagesSquare } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/*
 * Links into analytics screens that may live in route files built elsewhere (sessions, use cases,
 * paths). Paths are typed as plain strings so these links stay valid whatever the route tree holds;
 * the router resolves them at runtime like any other path.
 */

export const ROUTES = {
  sessions: "/sessions" as string,
  paths: "/paths" as string,
  session: (id: string): string => `/sessions/${id}`,
  useCase: (id: string): string => `/use-cases/${id}`,
  signalDefinition: (id: string): string => `/signals/studio/${id}`,
  funnel: (id: string): string => `/funnels/${id}`,
  issue: (id: string): string => `/issues/${id}`,
  agentScore: (id: string): string => `/agents/${id}/score`,
};

/** Session-list filters (mirrors SessionQuery minus paging). Empty values are dropped. */
export interface SessionFilter {
  agentId?: string;
  useCaseId?: string;
  outcome?: string;
  signal?: string;
  funnelId?: string;
  droppedAfterStep?: number;
  painPointId?: string;
}

export function sessionSearch(s: SessionFilter): Record<string, string | number> {
  return Object.fromEntries(Object.entries(s).filter(([, v]) => v !== undefined && v !== null && v !== "")) as Record<string, string | number>;
}

/** Monospace session id that opens the session view. */
export function SessionLink({ id, className, children }: { id: string; className?: string; children?: ReactNode }) {
  return (
    <Link
      to={ROUTES.session(id)}
      onClick={(e) => e.stopPropagation()}
      className={cn("inline-flex items-center gap-1 font-mono text-[11.5px] text-fg-2 hover:text-accent hover:underline", className)}
      title={`Open session ${id}`}
    >
      {children ?? id}
    </Link>
  );
}

/** "View N sessions →" link into the filtered session list. */
export function SessionsLink({ filter, children, className }: { filter: SessionFilter; children: ReactNode; className?: string }) {
  return (
    <Link to={ROUTES.sessions} search={sessionSearch(filter)} className={cn("inline-flex items-center gap-1 text-xs text-fg-2 hover:text-accent hover:underline [&_svg]:size-3.5", className)}>
      <MessagesSquare className="text-fg-3" aria-hidden />
      {children}
      <ArrowUpRight className="text-fg-3" aria-hidden />
    </Link>
  );
}
